import { useLocation } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { getPageProps as getWindowPageProps } from '@/shared/utils/window'
import api from '@/shared/utils/axios'

// In our Stencil / Inertia frontend, props could be delivered from the backend on the initial page load.
// In the SPA React frontend, data must be fetched separately from initial request for the template.
// This hook attempts to partially fill that gap by retrieving data set on the window by Rails and falling
// back to a traditional AJAX request if the request info is missing.
//
// Usage:
//
// const [pageProps, { loading }] = usePageProps<IPageProps>()
//
// return <div>Hello {pageProps?.callUrl}</div>
//
// Intention is to leverage page props for minimal amounts of data. For example, using page props to obtain
// a backend-derived `previousCallsUrl` and then using internal API for fetching previous calls.
export function usePageProps<T>(): { pageProps: Nullable<T>; loading: boolean } {
  const location = useLocation()

  const { data, isLoading } = useQuery({
    queryKey: ['pageProps', location.pathname],
    queryFn: async ({ queryKey: [, pathname] }) => {
      const windowProps = getWindowPageProps<T>(pathname)
      if (windowProps) {
        return windowProps
      }

      const { data } = await api.get<T>(`${pathname}.json`)
      return data
    },
  })

  return { pageProps: data, loading: isLoading }
}
